import { Link } from 'react-router-dom';
import FounderNote from '../components/FounderNote';
import ContactForm from '../components/ContactForm';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import { ShieldCheck, Sparkles, HeartHandshake, Star, RefreshCcw, Heart, ArrowRight } from 'lucide-react';

const LOGO_URL = "/api/files/jsfragrance/products/be3c5fd6-5c5e-4e28-9eca-fed1e5e06eea.png";

const TRUST_PILLARS = [
  {
    icon: Sparkles,
    title: 'Quality first',
    body: 'Long-lasting, premium-inspired fragrances chosen carefully for elegance and clean character — built to wear from your morning routine through long evenings.',
  },
  {
    icon: ShieldCheck,
    title: 'Honest & transparent',
    body: 'We tell you exactly what each scent is inspired by, what it costs, and what to expect. No vague marketing. No hidden disappointment.',
  },
  {
    icon: Heart,
    title: 'Affordable luxury',
    body: 'Designer-tier presence shouldn\'t demand designer-tier prices. We refuse to compromise the experience — only the markup.',
  },
  {
    icon: HeartHandshake,
    title: 'Customers we keep',
    body: 'Many of our buyers come back not because of an algorithm — but because of how they were treated the first time.',
  },
  {
    icon: Star,
    title: 'Built through real support',
    body: 'JS Fragrance grows because of real customers — your reviews, reposts, referrals, and trust. We don\'t take that lightly.',
  },
  {
    icon: RefreshCcw,
    title: 'Always improving',
    body: 'Every batch, every bottle, every interaction is an opportunity to be a little better than yesterday.',
  },
];

function AboutHardcoded() {
  return (
    <div data-testid="about-page" className="min-h-screen">
      <SEOHead
        title="About JS Fragrance"
        description="JS Fragrance is a Filipino luxury perfumery hand-blending EDP at 30% oil concentration (Extrait-level) in Manila. Read our story, mission, and craftsmanship."
        url="https://jsfragrances.com/about"
      />
      <BreadcrumbSchema crumbs={[{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }]} />
      {/* Hero */}
      <div className="relative bg-gradient-to-b from-[#0E0E0E] to-[#0A0A0A] py-20 sm:py-28 border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%]"
            style={{ background: 'radial-gradient(ellipse at center, rgba(28,125,63,0.5) 0%, transparent 60%)' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-4 font-sans">JS Fragrance · Manila</p>
          <h1 className="hero-headline text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] font-light text-white mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Affordable luxury,<br/>
            <span className="text-[#D4AF37] italic">felt every day.</span>
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto text-sm sm:text-base font-sans leading-relaxed">
            We believe a fragrance should never be a luxury reserved for special occasions.
            It should be the quiet confidence you carry into every room — and never run out of.
          </p>
        </div>
      </div>

      <FounderNote />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-20 space-y-16 stagger-children">
        {/* Logo */}
        <div className="flex justify-center">
          <img src={LOGO_URL} alt="JS Fragrance" className="h-24 sm:h-28 w-auto" />
        </div>

        {/* Our Story */}
        <section data-testid="about-story">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3 font-sans">Our story</p>
          <h2 className="text-3xl sm:text-4xl text-white font-light mb-6 leading-tight"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            It started with one simple goal.
          </h2>
          <div className="space-y-5 text-white/70 text-[15px] leading-[1.8] font-sans">
            <p>
              JS Fragrance began as a passion — collecting and appreciating luxury scents,
              and realizing that the people we loved most often couldn't afford to wear them every day.
              Original designer perfumes are beautiful. They're also financially impractical for daily use.
            </p>
            <p>
              That gap between <em className="text-white not-italic">how a great fragrance makes you feel</em> and
              <em className="text-white not-italic"> how often you can actually afford to feel it</em> is what
              we set out to close. So we built a brand around premium-inspired fragrances that hold up to designer-tier
              presence — without the designer-tier price tag.
            </p>
            <p>
              At JS Fragrance, fragrance isn't just a scent. It becomes part of your identity.
              Your confidence. Your mood. The memory you leave in a room. Whether you're heading to work,
              on a first date, attending a wedding, or simply choosing to feel good about yourself today —
              the right fragrance can shift your entire presence.
            </p>
            <p className="text-white/50 italic">
              Every detail of JS Fragrance — from scent selection to packaging to how we reply to your DMs —
              is built with dedication and genuine care.
            </p>
          </div>
        </section>

        {/* Why customers trust us */}
        <section data-testid="about-trust">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3 font-sans">Why customers trust us</p>
          <h2 className="text-3xl sm:text-4xl text-white font-light mb-8 leading-tight"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Six promises we keep.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TRUST_PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} data-testid={`trust-pillar-${i}`}
                  className="bg-[#141414] border border-white/8 p-5 hover:border-[#D4AF37]/30 transition-colors">
                  <Icon className="w-5 h-5 text-[#D4AF37] mb-3" />
                  <h4 className="text-white text-base font-sans font-medium mb-1.5">{p.title}</h4>
                  <p className="text-white/55 text-[13px] font-sans leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Mission · Vision */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="about-mission-vision">
          <div className="bg-[#0E0E0E] border-l-2 border-[#D4AF37] p-6">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2 font-sans">Our mission</p>
            <p className="text-white/80 text-[15px] font-sans leading-[1.7]">
              To provide high-quality inspired fragrances that help people feel more confident, attractive,
              and expressive — while keeping luxury scents affordable and accessible.
            </p>
          </div>
          <div className="bg-[#0E0E0E] border-l-2 border-[#1C7D3F] p-6">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#1C7D3F] mb-2 font-sans">Our vision</p>
            <p className="text-white/80 text-[15px] font-sans leading-[1.7]">
              To become one of the most trusted fragrance brands in the Philippines —
              known for quality scents, strong customer relationships, and affordable luxury.
            </p>
          </div>
        </section>

        {/* What makes us different */}
        <section data-testid="about-different">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3 font-sans">What makes us different</p>
          <h2 className="text-3xl text-white font-light mb-6 leading-tight"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            More than just perfume.
          </h2>
          <ul className="space-y-3">
            {[
              'Premium-inspired fragrances with elegant scent profiles',
              'Affordable pricing without compromising quality',
              'Stylish, professional presentation worth gifting',
              'Smooth customer experience — from inquiry to delivery',
              'Passion-driven brand built with genuine care for customers',
              'Continuously improving products and service',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-white/70 text-[15px] font-sans leading-relaxed">
                <span className="text-[#D4AF37] mt-2 text-[10px] flex-shrink-0">◆</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-white/50 text-sm font-sans italic mt-6 leading-relaxed">
            We want every customer to experience confidence, self-expression, and everyday luxury through
            scents they genuinely enjoy. Fragrances create memories, boost confidence, and leave lasting
            impressions — and we never forget that.
          </p>
        </section>

        {/* A message from JS Fragrance */}
        <section className="bg-gradient-to-br from-[#141414] to-[#0E0E0E] border border-[#D4AF37]/20 p-8 sm:p-10 text-center"
          data-testid="about-message">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3 font-sans">A message from JS Fragrance</p>
          <p className="text-2xl sm:text-3xl text-white font-light leading-snug mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            "To every customer who supports JS Fragrance — thank you for trusting our brand."
          </p>
          <p className="text-white/60 text-[14px] font-sans leading-[1.8] max-w-xl mx-auto">
            Whether you're a first-time buyer or a returning client, your support helps us continue growing
            and improving. We appreciate every order, every piece of feedback, every recommendation,
            and every repost. <br/><br/>
            We promise to keep providing quality fragrances, better experiences, and service
            that values every single customer.
          </p>
          <p className="text-white/40 text-xs uppercase tracking-[0.25em] font-sans mt-6">
            Thank you for being part of the journey.
          </p>
        </section>

        {/* CTA */}
        <section className="text-center pt-4">
          <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-sans mb-5">
            Find the scent that becomes yours.
          </p>
          <Link to="/shop"
            data-testid="about-shop-btn"
            className="btn-shimmer inline-flex items-center gap-2 bg-[#D4AF37] text-black px-8 py-3.5 text-xs uppercase tracking-[0.2em] font-sans font-bold hover:bg-[#E5C75A] transition-colors">
            Explore the collection <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        {/* Contact Form */}
        <section id="contact" className="pt-12 scroll-mt-24" aria-labelledby="contact-heading">
          <div className="text-center mb-8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2 font-sans">Get in touch</p>
            <h2 id="contact-heading" className="text-3xl sm:text-4xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Talk to us
            </h2>
          </div>
          <ContactForm />
        </section>
      </div>
    </div>
  );
}

export default function AboutPage() {
  return <AboutHardcoded />;
}
